import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Instagram, Send } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { SECTIONS, CONTACT } from "@/constants/testIds";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      toast.success(t.contact.success);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={SECTIONS.contact}
      className="relative bg-[#070707] text-white py-28 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#c5a572]" />
            <span className="text-[11px] tracking-[0.42em] text-[#c5a572] uppercase">
              {t.contact.eyebrow}
            </span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md font-light">{t.contact.body}</p>

          <div className="mt-12 space-y-7">
            <ContactRow
              icon={<MapPin size={18} />}
              label={t.contact.address}
              value="Mihajla Pupina 81, Jabuka, Pančevo"
              href="https://maps.google.com/?q=Mihajla+Pupina+81+Jabuka+Pancevo"
            />
            <ContactRow
              icon={<Phone size={18} />}
              label={t.contact.phoneLabel}
              value="064 668 8850"
              href="tel:+381646688850"
              testid="footer-phone"
            />
            <ContactRow
              icon={<Mail size={18} />}
              label={t.contact.emailLabel}
              value="zarkostanimirovic75@gmail.com"
              href="mailto:zarkostanimirovic75@gmail.com"
              testid="footer-email"
            />
            <ContactRow
              icon={<Instagram size={18} />}
              label={t.contact.instagram}
              value="@vina.stanimirovic"
              href="https://instagram.com/vina.stanimirovic"
              testid="footer-instagram"
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid={CONTACT.form}
            onSubmit={submit}
            className="bg-[#0d0d0d] border border-white/10 p-8 lg:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field
                testid={CONTACT.name}
                name="name"
                label={t.contact.name}
                value={form.name}
                onChange={onChange}
                required
              />
              <Field
                testid={CONTACT.email}
                name="email"
                type="email"
                label={t.contact.email}
                value={form.email}
                onChange={onChange}
                required
              />
              <Field
                testid={CONTACT.phone}
                name="phone"
                label={t.contact.phone}
                value={form.phone}
                onChange={onChange}
              />
              <Field
                testid={CONTACT.subject}
                name="subject"
                label={t.contact.subject}
                value={form.subject}
                onChange={onChange}
              />
            </div>
            <div className="mt-6">
              <label className="block text-[10px] tracking-[0.32em] uppercase text-white/50 mb-2">
                {t.contact.message}
              </label>
              <textarea
                data-testid={CONTACT.message}
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#c5a572] outline-none py-3 text-white font-light placeholder-white/30 transition-colors"
              />
            </div>

            <button
              data-testid={CONTACT.submit}
              type="submit"
              disabled={loading}
              className="mt-10 group inline-flex items-center gap-3 bg-[#c5a572] hover:bg-[#d4b685] disabled:opacity-60 text-[#0a0a0a] px-8 py-4 rounded-full text-[12px] tracking-[0.28em] uppercase font-medium transition-all duration-300"
            >
              {loading ? t.contact.sending : t.contact.send}
              <Send size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </button>

            {done && (
              <div data-testid={CONTACT.success} className="mt-6 text-[#c5a572] text-sm">
                {t.contact.success}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ testid, label, name, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.32em] uppercase text-white/50 mb-2">{label}</label>
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#c5a572] outline-none py-3 text-white font-light transition-colors"
      />
    </div>
  );
}

function ContactRow({ icon, label, value, href, testid }) {
  return (
    <a
      data-testid={testid}
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className="flex items-start gap-4 group"
    >
      <div className="mt-1 w-10 h-10 flex items-center justify-center border border-white/15 group-hover:border-[#c5a572] group-hover:text-[#c5a572] text-white/70 transition-colors">
        {icon}
      </div>
      <div>
        <div className="text-[10px] tracking-[0.32em] uppercase text-white/45">{label}</div>
        <div className="mt-1 text-white/90 group-hover:text-[#c5a572] transition-colors font-light">
          {value}
        </div>
      </div>
    </a>
  );
}
